import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '@app/finxone-web-frontend/app/Modules/shared/widgets/base/base.component';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { TransferReason } from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  FormActionState,
  FormActionStateModel,
  FormActionType,
  FormDataType,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ProviderClass,
  WidgetLayoutAttributes,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select } from '@ngxs/store';
import { FormActionTypeEnum } from 'libs/shared/enums/form-action.enum';
import { Observable, Subject, Subscription, catchError, map, of, switchMap, take, takeUntil } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-platform-payment-reason-widget',
  templateUrl: './payment-reason-widget.component.html',
  styleUrl: './payment-reason-widget.component.css',
})
export class PaymentReasonWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  @Select(AccountState.getAccounts)
  public accounts!: Observable<AccountStateModel>;

  @Select(FormActionState.getFormActionStateWithId(FormActionTypeEnum.INTL_TRANSFER_RESPONSE))
  formActionIntlTransfer!: Observable<FormActionType | null>;

  @Select(FormActionState.getFormActionState)
  formAction!: Observable<FormActionStateModel>;
  public paymentReasonForm: FormGroup;
  public inputFieldKey: string;
  public transferReasons: TransferReason[] = [];
  public paymentReasonCategories: TransferReason[] = [];
  public accountId: string;
  public transferCountry = 'GB'; // TODO: Hard code to GB for domestic payments for now
  public formActionData: FormDataType;

  public widgetStyle: baseCssConfigStyle;

  selectedText: TransferReason | undefined = {
    id: '',
    description: '',
  };
  selectedCategoryText: TransferReason | undefined;

  public inputLabel: string;
  public reasonCategoryLabel: string;
  private categorySubscription: Subscription;

  constructor(
    private accountService: AccountService,
    private configService: ConfigService,
    private formSubmissionService: FormSubmissionService,
    public dataIn: ProviderClass<WidgetLayoutAttributes>,
    private alertService: AlertHandlerService,
  ) {
    super();
    this.contentStyle = this.configService.onGetBaseWidgetStyle(this.dataIn.attri);

    this.widgetStyle = this.configService.onGetBaseWidgetStyle(this.dataIn.attri, false);

    this.inputFieldKey = this.dataIn.attri.widgetProperties['inputFieldKey'];

    if (this.dataIn?.attri.widgetProperties?.inputLabel) {
      this.inputLabel = this.dataIn?.attri.widgetProperties?.inputLabel;
    }

    if (this.dataIn?.attri['reasonCategoryLabel']) {
      this.reasonCategoryLabel = this.dataIn?.attri['reasonCategoryLabel'];
    }

    this.accounts
      .pipe(
        takeUntil(this.destroy$),
        map((accountState) => {
          this.accountId = accountState.currentAccountId;
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    if (this.dataIn.attri['enableReasonCategory']) {
      this.initPaymentReasonCategories();
    } else {
      this.initTransferReasons();
    }
    this.initForm();
  }

  private initPaymentReasonCategories() {
    this.formAction
      .pipe(
        take(1),
        switchMap((formAction: FormActionStateModel | null) => {
          this.formActionData = formAction?.response?.formData;
          this.transferCountry = formAction?.response?.formData['beneficiaryCountry'] || 'GB';
          // Fetch categories (first lookup)
          return this.accountService.getTransferReasons(this.accountId, this.transferCountry);
        }),
        catchError((error: any) => {
          this.alertService.showAlertFn('error', 'Cannot load payment reason categories');
          console.error('Cannot load payment reason categories:', error);
          return of([]);
        }),
      )
      .subscribe((categories: TransferReason[]) => {
        this.paymentReasonCategories = categories;
        const savedCategory = this.formActionData?.['paymentReasonCategory'];
        // Optionally, fetch reasons for the first category or leave transferReasons empty,
        // unless a previously saved category is being rehydrated concurrently in initForm()
        // - clearing here would race with that call and wipe the restored reasons list.
        if (!savedCategory) {
          this.transferReasons = [];
        } else {
          // The category options only just loaded, so the select had nothing to match
          // the already-set form control value against until now - re-affirm it so the
          // dropdown resolves and displays the saved category's label.
          this.paymentReasonForm?.get('paymentReasonCategory')?.setValue(savedCategory, { emitEvent: false });
          this.selectedCategoryText = this.paymentReasonCategories.find((obj) => obj.id === savedCategory);
        }
      });
  }

  private initTransferReasons(category?: string, isRehydrate = false) {
    if (!isRehydrate) {
      this.transferReasons = []; // Clear existing reasons
      this.selectedText = undefined; // Clear selected value
      this.paymentReasonForm?.get(this.inputFieldKey)?.setValue(''); // Clear form control value
      this.paymentReasonForm?.get(this.inputFieldKey + 'Text')?.setValue(''); // Clear description text
    }
    this.formAction
      .pipe(
        take(1),
        switchMap((formAction: FormActionStateModel | null) => {
          this.formActionData = formAction?.response?.formData;
          this.transferCountry = formAction?.response?.formData['beneficiaryCountry'] || 'GB';
          // If category is provided, pass it as param
          return this.accountService.getTransferReasons(this.accountId, this.transferCountry, category).pipe(
            catchError((error: any) => {
              this.alertService.showAlertFn('error', 'Cannot load transfer reasons');
              console.error('Cannot load transfer reasons:', error);
              return of([]);
            }),
          );
        }),
      )
      .subscribe((transferReasons: TransferReason[]) => {
        this.transferReasons = transferReasons;
        const savedPaymentReason = this.transferReasons?.find(
          (obj) => obj.id === this.formActionData?.[this.inputFieldKey],
        );
        if (savedPaymentReason) {
          this.selectedText = savedPaymentReason;
          if (isRehydrate) {
            // The reason options only just loaded for the saved category, so the form
            // control (already set from stored form data) had nothing to match against
            // until now - restore it so the select displays the saved reason.
            this.paymentReasonForm?.get(this.inputFieldKey)?.setValue(savedPaymentReason.id, { emitEvent: false });
          }
        }
      });
  }

  private initForm() {
    const savedCategory = this.formActionData?.['paymentReasonCategory'] ?? '';
    this.paymentReasonForm = new FormGroup({
      [this.inputFieldKey]: new FormControl(
        this.formActionData?.[this.inputFieldKey] ?? '',
        Validators.required,
      ),
      [this.inputFieldKey + 'Text']: new FormControl(
        this.formActionData?.[this.inputFieldKey + 'Text'] ?? '',
      ),
      paymentReasonCategory: new FormControl(savedCategory),
    });
    this.formSubmissionService.addCurrentPageFormGroups(this.paymentReasonForm);
    this.subscribeToFormChanges();

    // Subscribe to category changes if enabled
    if (this.dataIn.attri['enableReasonCategory']) {
      this.categorySubscription = this.paymentReasonForm
        .get('paymentReasonCategory')!
        .valueChanges.subscribe((category: string) => {
          if (category) {
            this.initTransferReasons(category);
          } else {
            this.transferReasons = [];
          }
        });

      // Rehydrate the transfer-reason options for a previously saved category so the
      // "Payment Reason" select can show/select the saved value again after navigating
      // back to this widget, instead of relying on user interaction to populate the list.
      if (savedCategory) {
        this.initTransferReasons(savedCategory, true);
      }
    }
  }

  private subscribeToFormChanges() {
    this.paymentReasonForm.valueChanges.subscribe(() => {
      const formIndex = this.formSubmissionService.currentPageFormGroups.findIndex((item) =>
        item.get(this.inputFieldKey),
      );
      this.formSubmissionService.updateCurrentPageFormGroups(formIndex, this.paymentReasonForm);
      this.formSubmissionService.submitIndividualForm(this.paymentReasonForm.value, '');
    });
  }

  onValueChange() {
    //this will read selected option from array
    this.selectedText = this.transferReasons.find(
      (obj) => obj.id === this.paymentReasonForm.get(this.inputFieldKey)?.value,
    );

    //update Text field with selected description
    this.paymentReasonForm.controls[this.inputFieldKey + 'Text'].setValue(this.selectedText?.description);
  }

  onCategoryValueChange() {
    //this will read selected category option from array so the select can display its label
    this.selectedCategoryText = this.paymentReasonCategories.find(
      (obj) => obj.id === this.paymentReasonForm.get('paymentReasonCategory')?.value,
    );
  }

  ngOnDestroy() {
    if (this.categorySubscription) {
      this.categorySubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
